/**
 * Streaming SHA-256 of a browser File.
 *
 * UI_PLAN §2.5 / FR-4: we need the digest of the file's bytes BEFORE we ship
 * the multipart upload.
 *
 * The digest is computed incrementally with the pure-JS `js-sha256`: every 1 MB
 * slice is fed to the hasher and released immediately, so the memory peak is
 * ≈ CHUNK_SIZE regardless of file size.
 *
 * We deliberately do NOT use `crypto.subtle.digest`: Web Crypto has no
 * incremental API, so the whole file would have to be accumulated in one
 * contiguous ArrayBuffer first. That allocation dies with
 * `RangeError: Array buffer allocation failed` well before the ~2 GB
 * ArrayBuffer ceiling — a 1.3 GB meeting recording already fails (TM-fb963707).
 * The old code only took that path in a secure context, so plain-HTTP dev
 * stands silently exercised a different implementation than production; one
 * path for every environment is also what keeps that class of bug away.
 *
 * Throughput is ~90 MB/s (≈15 s for 1.3 GB), an order of magnitude below the
 * native digest but negligible next to the upload it precedes. Slices are read
 * with `await`, so the event loop stays responsive between chunks.
 */

import { sha256 as jsSha256 } from "js-sha256";

const CHUNK_SIZE = 1024 * 1024;

export interface StreamSha256Options {
  /** Notifies the caller about progress (`processed`, `total`) in bytes. */
  onProgress?: (processed: number, total: number) => void;
  /** Optional abort. Throws `DOMException('Aborted', 'AbortError')` when fired. */
  signal?: AbortSignal;
}

function ensureNotAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

async function readSlice(slice: Blob): Promise<Uint8Array> {
  // `Blob.arrayBuffer()` is the modern path. jsdom and some older browsers
  // implement only `FileReader`, so we fall back to it explicitly. Either way
  // returns a fresh Uint8Array view that the hasher consumes and drops.
  if (typeof (slice as Blob).arrayBuffer === "function") {
    const buf = await slice.arrayBuffer();
    return new Uint8Array(buf);
  }
  const buf = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader error"));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(slice);
  });
  return new Uint8Array(buf);
}

/**
 * Compute the SHA-256 of `file` in 1 MB chunks. The `onProgress` callback is
 * fired after every chunk read (monotonically growing `processed`, capped at
 * `total`). Aborting the `signal` raises a DOMException synchronously on the
 * next chunk boundary — callers must `try/catch` if they want to swallow it.
 */
export async function streamSha256(
  file: File,
  options: StreamSha256Options = {},
): Promise<string> {
  const { onProgress, signal } = options;
  const total = file.size;
  ensureNotAborted(signal);

  const hasher = jsSha256.create();
  if (total === 0) {
    onProgress?.(0, 0);
    return hasher.hex();
  }

  let offset = 0;
  while (offset < total) {
    ensureNotAborted(signal);
    const end = Math.min(offset + CHUNK_SIZE, total);
    const chunk = await readSlice(file.slice(offset, end));
    hasher.update(chunk);
    offset = end;
    onProgress?.(offset, total);
  }
  ensureNotAborted(signal);
  return hasher.hex();
}
